import { Component, EventEmitter, Input, Output } from '@angular/core'
import { finalize } from 'rxjs'
import { ToastService } from '@common/services'
import { DemandIntentDocument } from '@models/demand/demand-intent-document.model'
import { NoticeSessionStatus } from '@models/planning/notice-session.model'
import { AccountService } from '@services/account.service'
import { AlertifyService } from '@services/alertify.service'
import { NoticeSessionService } from '@services/notice-session.service'
import { sessionOpeningCheck } from './notice-session-opening.util'

/**
 * Abrir, fechar e reabrir a sessão do edital — fora do Credenciamento (`demand.loose`), que tem as
 * próprias ações nos Participantes.
 *
 * Saiu do `session-enrollment` em 28/09/2026: com disputa, as ações ficam no cabeçalho da aba Disputa,
 * à direita das sub-abas, e valem de qualquer uma delas; sem disputa, continuam no topo dos
 * Participantes. Com o encerramento forçado (spec 024) só o indicativo fica — o estado é terminal.
 */
@Component({
	selector: 'app-notice-session-actions',
	templateUrl: './notice-session-actions.component.html',
	standalone: false
})
export class NoticeSessionActionsComponent {
	@Input() demand!: DemandIntentDocument
	/** Status confirmado pelo servidor; o `demand` recebido já sai com ele. */
	@Output() statusChange = new EventEmitter<NoticeSessionStatus>()

	submitted = false

	constructor(
		public accountService: AccountService,
		private toast: ToastService,
		private alertfy: AlertifyService,
		private noticeSessionService: NoticeSessionService
	) {}

	/**
	 * Pregão e Concorrência: a primeira abertura exige o edital publicado (29/09/2026) — a mesma exigência da inscrição
	 * nessas modalidades. O atalho do produto Dispensa Link, que mostra as ações antes da publicação, não vale para
	 * elas. A Dispensa, com ou sem disputa, fica de fora, e a reabertura segue livre.
	 */
	get openBlockedByNotice(): boolean {
		return this.demand?.isTradingOrCompetition && !!this.demand.noticeSession?.statusCreated && !this.demand.publishStatusDone
	}

	sessionOpened() {
		const { id } = this.demand.noticeSession

		if (this.openBlockedByNotice) {
			this.toast.warn('Publique o edital antes de abrir a sessão do Pregão ou da Concorrência')
			return
		}

		// A duração da fase de lances (`bidTimerMinutes`) é a fonte usada ao cadastrar/abrir fases na
		// Dispensa com disputa — sem essa trava, a sessão abre e as fases nascem com duração zero
		// quando a disputa (ex.: vinda do planejamento) nunca teve horas/minutos preenchidos. No
		// Pregão e na Concorrência o tempo é do motor de etapas (padrões da IN 73/2022) e o painel de
		// fases cai na etapa aberta do processo quando a sessão não o carrega (25/09/2026).
		if (this.demand.isDispensationDispute && !this.demand.noticeSession.bidTimerMinutes) {
			this.toast.warn('Informe as horas e minutos da fase de lance nos Dados iniciais da disputa antes de abrir a sessão')
			return
		}

		// PC-02/DSP-G02: a primeira abertura respeita o horário do edital — antes dele é recusada, e depois de 15 minutos
		// pede a justificativa do atraso, que vai para a ata. O servidor decide; aqui só se evita a ida e volta.
		const firstOpening = !!this.demand.noticeSession.statusCreated && !this.demand.loose
		const opening = firstOpening
			? sessionOpeningCheck(this.demand.noticeSession.closedDate, this.demand.noticeSession.closedTime)
			: { early: false, late: false, scheduledLabel: null }

		if (opening.early) {
			this.toast.warn(`A sessão só pode ser aberta a partir de ${opening.scheduledLabel}, o horário de abertura do edital`)
			return
		}

		if (opening.late) {
			this.alertfy.prompt(
				'Abrir Sessão',
				`O horário de abertura do edital era ${opening.scheduledLabel}. Informe a justificativa do atraso, que vai para a ata.`,
				event => {
					const justification = event.value?.trim()
					if (!event.confirmed) return
					if (!justification) {
						this.toast.warn('Informe a justificativa do atraso para abrir a sessão')
						return
					}
					this.open(id, justification)
				},
				{ ok: 'Abrir', cancel: 'Cancelar' }
			)
			return
		}

		this.alertfy.confirm(`Abrir Sessão`, 'Deseja realmente abrir a sessão?', event => {
			if (event.confirmed) this.open(id)
		})
	}

	private open(id: string, justification?: string) {
		this.submitted = true
		this.noticeSessionService
			.opened(id, this.demand.loose, justification)
			.pipe(finalize(() => (this.submitted = false)))
			.subscribe(() => {
				this.toast.success('Sessão aberta com sucesso!')
				this.demand.noticeSession.status = NoticeSessionStatus.OPENED
				this.statusChange.emit(NoticeSessionStatus.OPENED)
			})
	}

	sessionClosed() {
		const { id } = this.demand.noticeSession

		this.alertfy.confirm(`Fechar Sessão`, 'Deseja realmente fechar a sessão?', event => {
			if (event.confirmed) {
				this.submitted = true
				this.noticeSessionService
					.closed(id, this.demand.loose)
					.pipe(finalize(() => (this.submitted = false)))
					.subscribe(() => {
						this.toast.success('Sessão fechada com sucesso!')
						this.demand.noticeSession.status = NoticeSessionStatus.CLOSED
						this.statusChange.emit(NoticeSessionStatus.CLOSED)
					})
			}
		})
	}
}
